import { DOCUMENT } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import { ErrorHandler, inject, Injectable } from '@angular/core';
import { SentryLoggerService } from './sentry-logger.service';

@Injectable()
export class GlobalErrorHandlerService implements ErrorHandler {
  private document = inject(DOCUMENT);
  private sentryLogger = inject(SentryLoggerService);

  handleError(error: unknown): void {
    const normalizedError = this.normalizeError(error);

    if (this.isChunkLoadError(error)) {
      this.sentryLogger.captureException(normalizedError, {
        component: 'GlobalErrorHandler',
        action: 'chunkLoadError',
      });
      this.showChunkErrorOverlay();
      return;
    }

    if (this.isHttpError(error)) {
      this.sentryLogger.captureHttpError(error, {
        component: 'GlobalErrorHandler',
        action: 'unhandledHttpError',
      });
    } else {
      this.sentryLogger.captureException(normalizedError, {
        component: 'GlobalErrorHandler',
        action: 'unhandledError',
      });
    }

    console.error('Unhandled error:', error);
  }

  private showChunkErrorOverlay(): void {
    if (this.document.querySelector('.chunk-error-overlay')) {
      return;
    }

    const container = this.document.createElement('div');
    container.className = 'chunk-error-overlay';
    container.innerHTML = `
      <div style="position: fixed; inset: 0; background: #0a0a0a; z-index: 9999; display: flex; flex-direction: column; align-items: center; justify-content: center; padding: 2rem; text-align: center;">
        <span class="material-symbols-outlined" style="font-size: 80px; color: rgba(255,255,255,0.2); margin-bottom: 1rem;">cloud_off</span>
        <h2 style="color: white; font-size: 1.5rem; font-weight: 600; margin-bottom: 0.5rem;">Chyba na\u010D\u00EDtania</h2>
        <p style="color: rgba(255,255,255,0.6); margin-bottom: 1.5rem; max-width: 400px;">Niektor\u00E9 \u010Dasti aplik\u00E1cie sa nepodarilo na\u010D\u00EDta\u0165.</p>
        <button style="padding: 0.75rem 1.5rem; background: #d4f53c; color: #0a0a0a; border: none; border-radius: 0.5rem; font-weight: 500; cursor: pointer;">
          Obnovi\u0165 str\u00E1nku
        </button>
      </div>
    `;

    const button = container.querySelector('button');
    if (button) {
      button.addEventListener('click', async () => {
        try {
          const cacheNames = await caches.keys();
          await Promise.all(cacheNames.map(name => caches.delete(name)));
          const registrations = await navigator.serviceWorker.getRegistrations();
          await Promise.all(registrations.map(reg => reg.unregister()));
        } catch {}
        const retryUrl = sessionStorage.getItem('chunkErrorRetryUrl');
        sessionStorage.removeItem('chunkErrorRetryUrl');
        window.location.href = retryUrl || window.location.pathname;
      });
    }

    this.document.body.appendChild(container);
  }

  private normalizeError(error: unknown): Error {
    if (error instanceof Error) {
      return error;
    }

    if (this.isHttpError(error)) {
      const httpError = error as HttpErrorResponse;
      const normalizedError = new Error(`HTTP ${ httpError.status }: ${ httpError.message || httpError.statusText }`);
      normalizedError.name = 'HttpError';
      return normalizedError;
    }

    if (typeof error === 'object' && error !== null) {
      const errorObj = error as Record<string, unknown>;
      const message = errorObj['message'] || errorObj['error'] || JSON.stringify(error);
      const normalizedError = new Error(String(message));
      normalizedError.name = 'UnknownError';
      return normalizedError;
    }

    return new Error(String(error));
  }

  private isHttpError(error: unknown): boolean {
    return error instanceof HttpErrorResponse ||
      (typeof error === 'object' && error !== null && 'status' in error && 'url' in error && 'headers' in error);
  }

  private isChunkLoadError(error: unknown): boolean {
    if (error instanceof Error) {
      const message = error.message || '';
      return (
        message.includes('ChunkLoadError') ||
        message.includes('Loading chunk') ||
        message.includes('Failed to fetch dynamically imported module') ||
        message.includes('Importing a module script failed') ||
        message.includes('Load failed')
      );
    }

    if (typeof error === 'object' && error !== null) {
      const errorObj = error as Record<string, unknown>;
      const message = String(errorObj['message'] || errorObj['error'] || '');
      const rejection = errorObj['rejection'] as Record<string, unknown> | undefined;
      const rejectionMessage = rejection ? String(rejection['message'] || '') : '';

      return (
        message.includes('ChunkLoadError') ||
        message.includes('Loading chunk') ||
        message.includes('Failed to fetch dynamically imported module') ||
        message.includes('Importing a module script failed') ||
        message.includes('Load failed') ||
        rejectionMessage.includes('Load failed')
      );
    }

    return false;
  }
}
